import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FormControl, FormGroup } from '@angular/forms';
import { MeetingSchedulingData } from '../../../../shared/models/meetings.model';
import { HubspotMeetingsService } from '../../../../shared/services/hubspot/hubspot-meetings.service';
import { faCalendarDays, faCalendarPlus, faCalendar } from '@fortawesome/pro-duotone-svg-icons';

@Component({
  selector: 'app-meetings',
  templateUrl: './meetings.component.html',
  styleUrl: './meetings.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeetingsComponent implements OnInit {
  // Signals: sob zoneless, `.set()` notifica o agendador do Angular de forma confiável.
  // (Não dependemos do tick global do interceptor HTTP, que corre com o ciclo da navegação.)
  public schedulingData = signal<MeetingSchedulingData | null>(null);
  public loadingSchedulingData = signal(false);

  /**
   * E-mail cujos dados já foram carregados. Como navegar reemite o `queryParamMap`,
   * isso evita um segundo fetch reentrante (e o "piscar" da tela) após a busca pelo botão.
   */
  private _loadedForEmail: string | null = null;

  public meetingContactSelectionForm = new FormGroup({
    contactEmail: new FormControl(''),
  });

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _meetingsService: HubspotMeetingsService,
    private _toastr: ToastrService) {}

  public icons = {
    calendarDays: faCalendarDays,
    calendars: faCalendar,
    calendarCirclePlus: faCalendarPlus,
  };

  ngOnInit(): void {
    this._route.queryParamMap.subscribe(params => {
      const fromEmail = params.get('contato');
      this.meetingContactSelectionForm.get('contactEmail')?.setValue(fromEmail);

      if (!fromEmail) {
        this.schedulingData.set(null);
        this._loadedForEmail = null;
        return;
      }

      // Dados deste contato já carregados (ex.: busca pelo botão acabou de navegar) → não recarrega.
      if (this._loadedForEmail === fromEmail) {
        return;
      }

      // Deep-link direto (ex.: /reunioes/lista?contato=...): carrega os dados.
      this._loadAndRoute(fromEmail);
    });
  }

  public onOutletLoaded(component: any): void {
    component.schedulingData = this.schedulingData();
  }

  public sendMeetingContactSelectionForm(): void {
    const contactEmail = this.meetingContactSelectionForm.get('contactEmail')?.value;

    if (!contactEmail) {
      this._toastr.error('Ops! O e-mail do contato é obrigatório.');
      return;
    }

    this._loadAndRoute(contactEmail);
  }

  private _loadAndRoute(contactEmail: string): void {
    this.schedulingData.set(null);

    this.getSchedulingData(contactEmail, () => {
      const target = (this.schedulingData()?.meetings?.length ?? 0) > 0 ? '/reunioes/lista' : '/reunioes/nova';
      this._router.navigate([target], {
        queryParams: { contato: contactEmail },
        queryParamsHandling: 'merge',
      });
    });
  }

  public getSchedulingData(contact_email: string, callback: Function = () => {}): void {
    this.loadingSchedulingData.set(true);
    this._meetingsService.getMeetingScheduleData(contact_email).subscribe({
      next: data => {
        this.schedulingData.set(data);
        this._loadedForEmail = contact_email;
        this.loadingSchedulingData.set(false);
        callback();
      },
      error: error => {
        this.loadingSchedulingData.set(false);
        this._loadedForEmail = null;
        if (error.error?.error) {
          this._toastr.error(error.error.error);
        } else {
          this._toastr.error('Ops! Falha ao carregar os dados de agendamento de reunião.');
        }
      },
    });
  }
}
